// Shared studio picker — one presentational component, two variants:
//   'full-viewport' (Variant A): the popped full-screen treatment (prototype
//     .stage/.panel) used by workspace entry.
//   'centered' (Variant B): the centered modal (prototype .ap-overlay/.ap-modal)
//     replacing the header "add studio" chevron dropdown; renders
//     center-viewport, NOT anchored to the chevron (plan annotation #8).
// This component never navigates — consumers own enter vs switch navigation.
// Per rule: think-in-systems (ai.knowledge/global) — pure props in, JSX out.
// See knowledge: native-dialog-showmodal-pattern (studio.client) — native
// <dialog>.showModal() gives focus trap, Esc routing and inert background.
import type { ReactElement } from 'react';
import { Callout } from '@studio/design-system/callout';
import { useRegistryListAlert } from '#~/auth/use-registry-list-alert.js';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { twMerge } from 'cn';
import { getCurrentVendorBadgeIcon, vendorName } from '@studio/design-system/themes/vendor.js';
import type { Room } from '@studio/models/room';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { OrgSection } from './popped/org-section.jsx';
import { SetupStudioCard } from './popped/setup-studio-card.jsx';
import { StudioCard } from './popped/studio-card.jsx';
import { StudioScroller } from './popped/studio-scroller.jsx';
import { organizationGradientClassName } from './popped/org-palette.js';
import { duplicateDisambiguators, groupStudios } from './popped/organize.js';
import { sortStudios } from './popped/sort.js';
import type { OrganizationPresentation, StudioPickerDescriptor } from './types.js';
import type { AccountBootstrapPickerMode } from '#~/routes/root/account-bootstrap-decision.js';
import { MultiBridgeModal } from './multibridge-modal.js';
import { ReplacementModal } from './replacement-modal.jsx';
import { SetupNoticeModal } from './setup-notice-modal.jsx';

export type StudioPickerVariant = 'full-viewport' | 'centered';

export interface StudioPickerProps {
  readonly variant: StudioPickerVariant;
  readonly organizationPresentation: OrganizationPresentation;
  readonly descriptors: readonly StudioPickerDescriptor[];
  /** The studio the consumer is already in — marked, not hidden (Variant B). */
  readonly currentRoomId?: Room['id'] | undefined;
  /** Selection reports a room only; consumers own navigation. */
  readonly onSelect: (roomId: Room['id']) => void;
  /** Esc / close button / backdrop click. Omit to make the picker blocking. */
  readonly onCancel?: () => void;
  /** Overrides for the variant-default copy. */
  readonly title?: string;
  readonly subtitle?: string;
  readonly pickerMode?: AccountBootstrapPickerMode | undefined;
  /**
   * The name this computer's Bridge registers, which a new Studio is named
   * after. Every Bridge Setup surface that names the computer shows it.
   */
  readonly computerName?: string | undefined;
  readonly busy?: boolean;
  readonly busyRoomId?: Room['id'] | undefined;
  readonly operationStatus?: string | undefined;
  readonly errorMessage?: string | undefined;
  /** When set, create failures render on the hero button instead of the status line. */
  readonly createError?: boolean | undefined;
  readonly onCreate?: (() => void) | undefined;
  readonly onRetryCapacity?: ((roomId: Room['id']) => void) | undefined;
  readonly multiBridge?:
    | {
        readonly studioName: string;
        readonly machineNames: readonly string[];
        readonly onCancel: (reason: 'cancel' | 'backdrop' | 'escape') => void;
        readonly onConfirm: () => void;
      }
    | undefined;
  readonly offline?:
    | {
        readonly lastAttemptLabel: string;
        readonly retrying: boolean;
        readonly onRetry: () => void;
        readonly onNetworkSettings?: (() => void) | undefined;
      }
    | undefined;
  /** Consent to replace an occupied Studio's Desktop Bridge; rendered by both variants. */
  readonly replacement?:
    | {
        readonly machineNames: readonly string[];
        readonly onCancel: (reason: 'cancel' | 'close' | 'backdrop' | 'escape') => void;
        readonly onConfirm: () => void;
      }
    | undefined;
  readonly notice?:
    | {
        readonly kind: 'permission';
        readonly studioName: string;
        readonly roleLabel?: string | undefined;
        readonly onBack: () => void;
        readonly onCreate: () => void;
      }
    | {
        readonly kind: 'link_failed';
        readonly studioName: string;
        readonly onCancel: () => void;
        readonly onRetry: () => void;
      }
    | {
        // ENG-4483 W3: occupied Studio, addition gated. Explanation only.
        readonly kind: 'add_unavailable';
        readonly studioName: string;
        readonly machineNames: readonly string[];
        readonly onBack: () => void;
        readonly onCreate: () => void;
      }
    | undefined;
}

const WORKSPACE_TITLE = 'Choose a studio';
const WORKSPACE_SUBTITLE = 'You belong to more than one studio. Pick one to jump in.';
const SETUP_TITLE = "Looks like you're on a new machine";
const SETUP_COMPUTER_FALLBACK = 'this computer';

const PlusIcon = (): ReactElement => (
  <svg aria-hidden='true' className='size-[15px] shrink-0 fill-current' viewBox='0 0 24 24'>
    <path d='M21 11.2h-8.2V3c0-.4-.3-.8-.8-.8s-.8.4-.8.8v8.2H3c-.4 0-.8.3-.8.8s.3.8.8.8h8.2V21c0 .4.3.8.8.8s.8-.3.8-.8v-8.2H21c.4 0 .8-.3.8-.8s-.4-.8-.8-.8' />
  </svg>
);

const Spinner = ({ className }: { readonly className: string }): ReactElement => (
  <span
    aria-hidden='true'
    className={twMerge(
      'shrink-0 animate-spin rounded-full border-2 border-white/20 border-t-focus',
      className
    )}
  />
);

const CreateStudioAction = ({
  busy,
  className,
  onCreate
}: {
  readonly busy: boolean;
  readonly className?: string;
  readonly onCreate: () => void;
}): ReactElement => (
  <button
    type='button'
    title='Create a new studio'
    disabled={busy}
    className={twMerge(
      'flex h-[38px] items-center gap-[7px] rounded-6 bg-primary px-16 text-[13px] font-demibold text-light shadow-[0_8px_24px_rgba(0,0,0,0.35)]',
      'disabled:opacity-60 focus-visible:outline-none focus-visible:ring hover:enabled:bg-primary-highlight disabled:cursor-wait',
      className
    )}
    {...STagNew(Sel.StudioPicker, 'create_studio')}
    onClick={onCreate}
  >
    <svg aria-hidden='true' width='15' height='15' viewBox='0 0 16 16' fill='none'>
      <path d='M8 3.5v9M3.5 8h9' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
    </svg>
    Create studio
  </button>
);

const CreateFailIcon = (): ReactElement => (
  <svg aria-hidden='true' className='size-[15px] shrink-0 fill-error' viewBox='0 0 24 24'>
    <path d='M12 1.2C6.1 1.2 1.2 6.1 1.2 12S6.1 22.8 12 22.8 22.8 17.9 22.8 12 17.9 1.2 12 1.2m0 20c-5.1 0-9.2-4.1-9.2-9.2S6.9 2.8 12 2.8s9.2 4.1 9.2 9.2-4.1 9.2-9.2 9.2m0-8.2c.6 0 1-.4 1-1V8c0-.6-.4-1-1-1s-1 .4-1 1v4c0 .6.4 1 1 1m0 2c-.6 0-1 .4-1 1s.5 1 1 1 1-.4 1-1-.4-1-1-1' />
  </svg>
);

const HeroCreateAction = ({
  busy,
  creating,
  computerName,
  createError,
  errorMessage,
  linking,
  onCreate
}: {
  readonly busy: boolean;
  readonly creating: boolean;
  readonly computerName: string;
  readonly createError: boolean;
  readonly errorMessage: string | undefined;
  readonly linking: boolean;
  readonly onCreate: () => void;
}): ReactElement => {
  if (creating) {
    return (
      <div className='flex flex-col items-center gap-10'>
        <div
          className='flex h-[44px] w-max items-center justify-center gap-10 whitespace-nowrap rounded-6 bg-base-100 px-[22px] text-[14px] font-demibold text-light shadow-[inset_0_0_0_1px_rgba(255,255,255,0.10)]'
          {...DataSTag(Sel.StudioPicker, 'create_progress')}
        >
          <Spinner className='size-[14px]' />
          Creating {computerName}…
        </div>
        <p className='text-[12px] text-secondary'>Linking this bridge, then opening the studio.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center gap-10'>
      {createError && (
        <div
          role='alert'
          className='flex items-center gap-10 rounded-6 bg-error/10 px-[14px] py-10 shadow-[inset_0_0_0_1px_rgb(var(--iris-error)/0.28)]'
          {...DataSTag(Sel.StudioPicker, 'operation_error')}
        >
          <CreateFailIcon />
          <span className='text-[13px] text-light'>
            {errorMessage ?? "Couldn't create the studio."}
          </span>
          <button
            type='button'
            className='hover:text-focus/80 shrink-0 px-2 text-[13px] font-demibold text-focus focus-visible:outline-none focus-visible:ring'
            {...STagNew(Sel.StudioPicker, 'create_retry')}
            onClick={onCreate}
          >
            Try again
          </button>
        </div>
      )}
      <div
        className={twMerge(
          'flex flex-col items-center gap-10 transition-opacity duration-[180ms]',
          linking && 'pointer-events-none opacity-[0.3]'
        )}
      >
        <button
          type='button'
          title='Create a new studio'
          disabled={busy}
          className={twMerge(
            'flex h-[44px] w-max shrink-0 items-center justify-center gap-[9px] whitespace-nowrap rounded-6 bg-primary px-[22px] text-[14px] font-demibold text-white',
            'transition-colors duration-[120ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]',
            'hover:enabled:bg-primary-highlight focus-visible:outline-none focus-visible:ring focus-visible:ring-focus/50',
            'disabled:cursor-wait disabled:opacity-50'
          )}
          {...STagNew(Sel.StudioPicker, 'create_studio')}
          onClick={onCreate}
        >
          <PlusIcon />
          Create a new studio
        </button>
        <p className='text-[12px] text-secondary'>
          Your studio will be named after this computer and will link to your bridge automatically.
        </p>
      </div>
    </div>
  );
};

const OperationFeedback = ({
  errorMessage,
  operationStatus
}: {
  readonly errorMessage: string | undefined;
  readonly operationStatus: string | undefined;
}): ReactElement => (
  <>
    {operationStatus !== undefined && (
      <p
        aria-live='polite'
        className='mt-16 text-center text-[13px] font-demibold text-accent'
        {...DataSTag(Sel.StudioPicker, 'operation_status')}
      >
        {operationStatus}
      </p>
    )}
    {errorMessage !== undefined && (
      <p
        role='alert'
        className='text-danger mx-auto mt-16 max-w-[520px] text-center text-[13px]'
        {...DataSTag(Sel.StudioPicker, 'operation_error')}
      >
        {errorMessage}
      </p>
    )}
  </>
);

/** Shared popped studio picker (Variant A full-viewport / Variant B centered). */
export const StudioPicker = ({
  variant,
  organizationPresentation,
  descriptors,
  currentRoomId,
  onSelect,
  onCancel,
  title,
  subtitle,
  pickerMode,
  computerName,
  busy = false,
  busyRoomId,
  operationStatus,
  errorMessage,
  createError = false,
  onCreate,
  onRetryCapacity,
  multiBridge,
  offline,
  replacement,
  notice
}: StudioPickerProps): ReactElement => {
  const listAlert = useRegistryListAlert('studios', offline === undefined);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const idSeed = useId().replaceAll(':', '');
  const titleId = `studio-picker-title-${idSeed}`;
  const descriptionId = `studio-picker-description-${idSeed}`;

  const groups = useMemo(() => groupStudios(descriptors), [descriptors]);
  const sortedDescriptors = useMemo(() => sortStudios(descriptors), [descriptors]);
  const disambiguators = useMemo(() => duplicateDisambiguators(descriptors), [descriptors]);
  const activeVendorName = vendorName();

  // showModal() (not open=) so we get the focus trap + inert background free.
  useEffect(() => {
    const dialog = dialogRef.current;

    if (dialog === null || dialog.open) {
      return;
    }

    dialog.showModal();
  }, []);

  // <dialog> fires `cancel` on Esc: block the native auto-close and route
  // through onCancel so blocking consumers (no onCancel) can suppress Esc.
  const handleCancel = useCallback(
    (event: React.SyntheticEvent): void => {
      event.preventDefault();
      if (!busy) onCancel?.();
    },
    [busy, onCancel]
  );

  const groupedRows = (
    <div className='flex flex-col gap-[26px]' {...DataSTag(Sel.StudioPicker, 'organizations')}>
      {groups.map((group) => (
        <OrgSection
          key={group.key}
          organization={group.organization}
          studios={group.studios}
          disambiguators={disambiguators}
          currentRoomId={currentRoomId}
          nudge={variant === 'full-viewport'}
          actionLabel={pickerMode === 'bridge_setup' ? 'Link' : 'Open'}
          disabled={busy}
          onSelect={onSelect}
        />
      ))}
    </div>
  );

  const hiddenRow = (
    <div {...DataSTag(Sel.StudioPicker, 'organizations')}>
      <StudioScroller nudge={variant === 'full-viewport'}>
        {sortedDescriptors.map((descriptor) => (
          <StudioCard
            key={descriptor.room.id}
            avatarGradientClassName={organizationGradientClassName(
              descriptor.organization,
              descriptor.room.organizationId
            )}
            descriptor={descriptor}
            disambiguator={disambiguators.get(descriptor.room.id)}
            isCurrent={currentRoomId !== undefined && descriptor.room.id === currentRoomId}
            actionLabel={pickerMode === 'bridge_setup' ? 'Link' : 'Open'}
            disabled={busy}
            onSelect={onSelect}
          />
        ))}
      </StudioScroller>
    </div>
  );

  const loadError = listAlert.visible ? (
    <Callout
      severity='warning'
      role='status'
      title="Couldn't load your studios"
      {...DataSTag(Sel.StudioPicker, 'load_error')}
    >
      <button
        type='button'
        disabled={listAlert.retrying}
        aria-busy={listAlert.retrying}
        onClick={() => {
          void listAlert.retry();
        }}
        className='focus-visible:ring-primary/50 mt-12 flex items-center gap-8 rounded border border-muted bg-base-100 px-12 py-6 text-foreground focus-visible:ring-2 disabled:opacity-50'
        {...STagNew(Sel.StudioPicker, 'load_retry')}
      >
        {listAlert.retrying && <Spinner className='size-[14px]' />}
        Try again
      </button>
    </Callout>
  ) : null;
  const studioRows =
    loadError ?? (organizationPresentation === 'grouped' ? groupedRows : hiddenRow);
  // One name on every surface: a second name for the same machine (the macOS
  // LocalHostName) contradicts the name the Studio is created with.
  const resolvedComputerName = computerName?.trim() || SETUP_COMPUTER_FALLBACK;
  const creating = busy && busyRoomId === undefined && onCreate !== undefined && !createError;
  const linking = busyRoomId !== undefined;
  const studioCount = sortedDescriptors.length;
  const setupGridClass =
    studioCount <= 1
      ? 'mx-auto max-w-[224px] grid-cols-1'
      : studioCount === 2
        ? 'mx-auto max-w-[460px] grid-cols-2'
        : 'grid-cols-[repeat(auto-fill,minmax(208px,1fr))]';

  if (variant === 'full-viewport' && pickerMode === 'bridge_setup' && offline !== undefined) {
    return (
      <dialog
        ref={dialogRef}
        onCancel={handleCancel}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={offline.retrying}
        className='fixed inset-0 h-full max-h-none w-full max-w-none overflow-hidden bg-dark p-0 text-light backdrop:bg-transparent'
        data-picker-mode={pickerMode}
        {...STagNew(Sel.StudioPicker, 'offline')}
      >
        <div className='flex min-h-full flex-col items-center justify-center px-24 text-center'>
          <span className='bg-error/15 mb-10 flex size-40 items-center justify-center rounded-full'>
            <svg aria-hidden='true' className='size-20 fill-error' viewBox='0 0 24 24'>
              <path d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 5.2c.6 0 1 .4 1 1V13c0 .6-.4 1-1 1s-1-.4-1-1V8.2c0-.6.4-1 1-1Zm0 10.1a1.2 1.2 0 1 1 0-2.4 1.2 1.2 0 0 1 0 2.4Z' />
            </svg>
          </span>
          <p className='mb-10 text-[10px] font-demibold uppercase tracking-[0.08em] text-secondary'>
            Offline
          </p>
          <h1 id={titleId} className='text-[28px] font-demibold leading-[1.15]'>
            Can&apos;t reach Iris
          </h1>
          <p
            id={descriptionId}
            className='mt-10 max-w-[420px] text-[14px] leading-[1.45] text-muted'
          >
            {resolvedComputerName} can&apos;t reach the Iris service, so your studios can&apos;t be
            loaded.
          </p>
          <div className='mt-24 flex justify-center gap-10'>
            <button
              type='button'
              disabled={offline.retrying}
              className='h-[38px] rounded-6 bg-primary px-16 text-[13px] font-demibold text-white hover:bg-primary-highlight focus-visible:outline-none focus-visible:ring disabled:cursor-wait disabled:opacity-50'
              {...STagNew(Sel.StudioPicker, 'offline_retry')}
              onClick={offline.onRetry}
            >
              {offline.retrying ? 'Trying again…' : 'Try again'}
            </button>
            {offline.onNetworkSettings !== undefined && (
              <button
                type='button'
                disabled={offline.retrying}
                className='h-[38px] rounded-6 border border-white/10 bg-base-100 px-16 text-[13px] font-medium text-light hover:bg-base-50 focus-visible:outline-none focus-visible:ring disabled:opacity-50'
                {...STagNew(Sel.StudioPicker, 'network_settings')}
                onClick={offline.onNetworkSettings}
              >
                Network settings
              </button>
            )}
          </div>
          <p className='mt-14 text-[11px] text-secondary'>
            Last attempt {offline.lastAttemptLabel} · retrying automatically
          </p>
        </div>
      </dialog>
    );
  }

  if (variant === 'full-viewport') {
    const isBridgeSetup = pickerMode === 'bridge_setup';
    return (
      <dialog
        ref={dialogRef}
        onCancel={handleCancel}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={busy}
        className='fixed inset-0 h-full max-h-none w-full max-w-none overflow-y-auto overflow-x-hidden bg-dark p-0 text-light backdrop:bg-transparent'
        data-picker-mode={pickerMode}
        {...STagNew(Sel.StudioPicker, 'full_viewport_modal')}
      >
        <div
          className='relative mx-auto flex min-h-full w-full max-w-[820px] flex-col px-24 pb-64'
          {...DataSTag(Sel.StudioPicker, 'stage')}
        >
          <div className='flex items-center justify-between gap-16 pt-18'>
            {isBridgeSetup ? (
              <div
                className='flex h-[30px] items-center gap-[9px] rounded-full bg-base-200 py-0 pl-10 pr-12 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]'
                {...DataSTag(Sel.StudioPicker, 'this_computer')}
              >
                <span aria-hidden='true' className='size-6 rounded-full bg-success' />
                <span className='text-[12px] text-muted'>This computer</span>
                {resolvedComputerName !== SETUP_COMPUTER_FALLBACK && (
                  <span
                    className='text-[12px] font-medium text-light'
                    {...DataSTag(Sel.StudioPicker, 'this_computer_name')}
                  >
                    {resolvedComputerName}
                  </span>
                )}
              </div>
            ) : (
              <span />
            )}
            <div className='text-[11px] tracking-[0.02em] text-secondary'>
              {isBridgeSetup ? 'One-time setup' : 'Studio access'}
            </div>
          </div>

          <div className='flex flex-1 flex-col pt-[44px]' {...DataSTag(Sel.StudioPicker, 'panel')}>
            <div className='text-center'>
              {/* The real vendor brand mark, not a hand-drawn approximation. The
                  only colored Iris mark shipped as an asset is the PNG badge:
                  `iris.svg` embeds the mark as a raster inside a mask and
                  `iris_badge_black_white.svg` is monochrome, so no vector
                  colored mark exists to prefer over the PNG. Per rule:
                  ui-architecture (ai.knowledge/global) — Hive builds get the
                  Hive badge via the vendor helper. */}
              <img
                src={getCurrentVendorBadgeIcon()}
                alt={`${activeVendorName} logo`}
                className='mx-auto mb-18 block size-[44px] object-contain'
                {...DataSTag(Sel.StudioPicker, 'vendor_mark')}
              />
              <p className='mb-10 text-[10px] font-demibold uppercase tracking-[0.08em] text-secondary'>
                {isBridgeSetup ? 'Bridge setup' : 'Studio access'}
              </p>
              <h1
                id={titleId}
                className='mb-10 text-[28px] font-demibold leading-[1.15] tracking-[-0.01em]'
              >
                {title ?? (isBridgeSetup ? SETUP_TITLE : WORKSPACE_TITLE)}
              </h1>
              <p
                id={descriptionId}
                className='mx-auto max-w-[430px] text-[14px] leading-[1.45] text-muted'
              >
                {subtitle ??
                  (isBridgeSetup
                    ? "This computer's bridge isn't linked to a studio yet. Create a new one, or link it to a studio you already have."
                    : WORKSPACE_SUBTITLE)}
              </p>
            </div>

            {isBridgeSetup && onCreate !== undefined && (
              <div className='mt-[30px] flex justify-center'>
                <HeroCreateAction
                  busy={busy}
                  creating={creating}
                  computerName={resolvedComputerName}
                  createError={createError}
                  errorMessage={errorMessage}
                  linking={linking}
                  onCreate={onCreate}
                />
              </div>
            )}

            <div className='mb-18 mt-[34px] flex items-center gap-12'>
              <span className='h-px flex-1 bg-white/[0.08]' />
              <span className='whitespace-nowrap text-[11px] font-medium tracking-[0.04em] text-secondary'>
                {isBridgeSetup ? 'Or connect to an existing studio' : 'Available studios'}
              </span>
              <span className='h-px flex-1 bg-white/[0.08]' />
            </div>

            {loadError ?? (
              <div
                className={twMerge('grid gap-10', setupGridClass)}
                {...DataSTag(Sel.StudioPicker, 'organizations')}
              >
                {sortedDescriptors.map((descriptor) => (
                  <SetupStudioCard
                    key={descriptor.room.id}
                    descriptor={descriptor}
                    disambiguator={disambiguators.get(descriptor.room.id)}
                    disabled={busy}
                    linking={busyRoomId === descriptor.room.id}
                    dimmed={creating || (linking && busyRoomId !== descriptor.room.id)}
                    selectionOnly={!isBridgeSetup}
                    computerName={resolvedComputerName}
                    onSelect={onSelect}
                    onRetryCapacity={onRetryCapacity}
                  />
                ))}
              </div>
            )}

            {!createError && (
              <OperationFeedback
                errorMessage={errorMessage}
                operationStatus={linking || creating ? undefined : operationStatus}
              />
            )}

            <p
              className='text-secondary/80 mt-28 text-center text-[11px] leading-[1.5]'
              {...DataSTag(Sel.StudioPicker, 'setup_footer')}
            >
              {isBridgeSetup
                ? `This is a one-time setup for ${resolvedComputerName}. ${activeVendorName} will open this studio automatically from then on.`
                : 'You can switch studios any time from the header.'}
            </p>
          </div>
        </div>
        {replacement !== undefined && (
          <ReplacementModal
            machineNames={replacement.machineNames}
            busy={busy}
            onCancel={replacement.onCancel}
            onConfirm={replacement.onConfirm}
          />
        )}
        {multiBridge !== undefined && (
          <MultiBridgeModal
            studioName={multiBridge.studioName}
            machineNames={multiBridge.machineNames}
            busy={busy}
            onCancel={multiBridge.onCancel}
            onConfirm={multiBridge.onConfirm}
          />
        )}
        {notice !== undefined && (
          <SetupNoticeModal
            kind={notice.kind}
            studioName={notice.studioName}
            computerName={resolvedComputerName}
            roleLabel={notice.kind === 'permission' ? notice.roleLabel : undefined}
            machineNames={notice.kind === 'add_unavailable' ? notice.machineNames : undefined}
            busy={busy}
            onSecondary={notice.kind === 'link_failed' ? notice.onCancel : notice.onBack}
            onPrimary={notice.kind === 'link_failed' ? notice.onRetry : notice.onCreate}
          />
        )}
      </dialog>
    );
  }

  if (variant === 'centered') {
    return (
      <dialog
        ref={dialogRef}
        onCancel={handleCancel}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={busy}
        // Center-viewport: the ::backdrop dims the app while native dialog keeps focus contained.
        className='fixed inset-0 m-auto w-full max-w-[820px] overflow-x-hidden rounded-[16px] border border-white/[0.18] bg-base-200 p-0 text-light shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.4)] backdrop:bg-black/[0.6] backdrop:backdrop-blur-[3px]'
        // Backdrop clicks land on the <dialog> element itself (not children).
        onClick={(event): void => {
          if (event.target === dialogRef.current && !busy) onCancel?.();
        }}
        {...STagNew(Sel.StudioPicker, 'centered_modal')}
      >
        <div className='flex items-center gap-12 border-b border-white/5 px-16 py-16'>
          <div>
            <div id={titleId} className='text-[15px] font-demibold text-light'>
              {title ?? 'Open a studio'}
            </div>
            <div id={descriptionId} className='text-[12px] text-secondary'>
              {subtitle ?? 'Pick a studio to switch to it.'}
            </div>
          </div>
          {onCreate !== undefined && (
            <CreateStudioAction className='ml-auto' busy={busy} onCreate={onCreate} />
          )}
          {onCancel && (
            <button
              type='button'
              aria-label='Close studio picker'
              disabled={busy}
              {...STagNew(Sel.StudioPicker, 'close')}
              onClick={onCancel}
              className={twMerge(
                'flex h-[30px] w-[30px] items-center justify-center rounded-6 text-muted transition-colors duration-250 hover:bg-base-50 hover:text-light focus-visible:outline-none focus-visible:ring disabled:cursor-wait disabled:opacity-50',
                onCreate === undefined && 'ml-auto'
              )}
            >
              <svg aria-hidden='true' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                <path
                  d='M4 4l8 8M12 4l-8 8'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                />
              </svg>
            </button>
          )}
        </div>
        <div className='max-h-[70vh] overflow-y-auto overflow-x-hidden px-16 pb-20 pt-8'>
          <OperationFeedback errorMessage={errorMessage} operationStatus={operationStatus} />
          {studioRows}
        </div>
        {/* Inside the <dialog>: showModal() marks everything OUTSIDE it inert
              and paints the dialog in the top layer, so a sibling overlay would
              be unclickable. Mirrors the full-viewport variant above. */}
        {replacement !== undefined && (
          <ReplacementModal
            machineNames={replacement.machineNames}
            busy={busy}
            onCancel={replacement.onCancel}
            onConfirm={replacement.onConfirm}
          />
        )}
      </dialog>
    );
  }

  throw new Error(`Unsupported Studio picker variant: ${variant satisfies never}`);
};
